import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GoogleMap, InfoWindowF, MarkerClustererF, MarkerF } from '@react-google-maps/api'
import { useMapsReady } from '../hooks/useMapsReady'
import MapUnavailable from '../components/MapUnavailable'
import { AlertCircle, Bike, RefreshCw } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'

import OrderDetailPanel from '../components/orders/OrderDetailPanel'
import { useColors } from '../hooks/useColors'
import { useSocket } from '../hooks/useSocket'
import { adminApi, type OrdersMapOrder } from '../services/api'

type LiveAgent = {
  id: string
  name: string | null
  phone: string | null
  current_lat: number | null
  current_lng: number | null
  last_seen_at: string | null
  zone: string | null
  active_orders_count: number
  lifecycle_status?: 'idle' | 'assigned' | 'en_route' | 'picked_up' | 'out_for_delivery'
}

type LocationEventPayload = {
  agent_id?: string
  lat?: number | string | null
  lng?: number | string | null
  recorded_at?: string
}

const DEFAULT_MAP_CENTER = { lat: 28.4595, lng: 77.0266 }
const MAP_CONTAINER_STYLE = { width: '100%', height: '640px' }
const REFRESH_INTERVAL_MS = 30000

const COLOR_IDLE = '#9CAB9A'
const COLOR_ASSIGNED = '#7C9FD6'
const COLOR_EN_ROUTE = '#D6B97B'
const COLOR_PICKED_UP = '#C77D5E'
const COLOR_OUT_FOR_DELIVERY = '#0F766E'
const COLOR_ORDER = '#0F2E2A'

const buildAgentIcon = (color: string, opacity = 1): google.maps.Symbol => ({
  path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z',
  fillColor: color,
  fillOpacity: opacity,
  strokeColor: '#FFFFFF',
  strokeWeight: 1.5,
  scale: 1.4,
})

const buildOrderIcon = (opacity = 1): google.maps.Symbol => ({
  path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z',
  fillColor: COLOR_ORDER,
  fillOpacity: opacity,
  strokeColor: '#FFFFFF',
  strokeWeight: 1.5,
  scale: 1,
})

const formatLastSeen = (iso: string | null): string => {
  if (!iso) return 'unknown'
  const ms = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(ms)) return 'unknown'
  const seconds = Math.floor(ms / 1000)
  if (seconds < 30) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
}

type AnimatedMarkerProps = {
  targetLat: number
  targetLng: number
  icon: google.maps.Symbol
  onClick: () => void
  clusterer?: Parameters<typeof MarkerF>[0]['clusterer']
  durationMs: number
  reduceMotion: boolean
}

function AnimatedAgentMarker({ targetLat, targetLng, icon, onClick, clusterer, durationMs, reduceMotion }: AnimatedMarkerProps) {
  const [currentLat, setCurrentLat] = useState(targetLat)
  const [currentLng, setCurrentLng] = useState(targetLng)
  const animRef = useRef<number | null>(null)

  useEffect(() => {
    if (currentLat === targetLat && currentLng === targetLng) return
    if (reduceMotion) {
      setCurrentLat(targetLat)
      setCurrentLng(targetLng)
      return
    }

    const startLat = currentLat
    const startLng = currentLng
    const startTime = performance.now()
    const duration = Math.max(300, Math.min(durationMs, 5000))

    const animate = (time: number) => {
      const elapsed = time - startTime
      const progress = Math.min(elapsed / duration, 1)
      setCurrentLat(startLat + (targetLat - startLat) * progress)
      setCurrentLng(startLng + (targetLng - startLng) * progress)
      if (progress < 1) {
        animRef.current = requestAnimationFrame(animate)
      }
    }

    animRef.current = requestAnimationFrame(animate)
    return () => {
      if (animRef.current !== null) cancelAnimationFrame(animRef.current)
    }
  }, [currentLat, currentLng, targetLat, targetLng, durationMs, reduceMotion])

  return (
    <MarkerF
      position={{ lat: currentLat, lng: currentLng }}
      icon={icon}
      onClick={onClick}
      clusterer={clusterer || undefined}
    />
  )
}

export default function LiveMapPage() {
  const colors = useColors()
  const [searchParams, setSearchParams] = useSearchParams()
  const [agents, setAgents] = useState<LiveAgent[]>([])
  const [orders, setOrders] = useState<OrdersMapOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null)
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)
  const [agentAnimationDuration, setAgentAnimationDuration] = useState<Record<string, number>>({})
  const [fadingOrderIds, setFadingOrderIds] = useState<Record<string, true>>({})
  const [fadingAgentIds, setFadingAgentIds] = useState<Record<string, true>>({})
  const [reduceMotion, setReduceMotion] = useState(false)
  const mapRef = useRef<google.maps.Map | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const apply = () => setReduceMotion(media.matches)
    apply()
    media.addEventListener('change', apply)
    return () => media.removeEventListener('change', apply)
  }, [])

  const filterStatus = searchParams.get('status') || 'all'
  const filterZone = searchParams.get('zone') || 'all'
  const filterAgent = searchParams.get('agent') || 'all'
  const filterSearch = searchParams.get('search') || ''

  const [mapCenter, setMapCenter] = useState(() => {
    try {
      const stored = sessionStorage.getItem('rapidry_livemap_center')
      return stored ? JSON.parse(stored) : DEFAULT_MAP_CENTER
    } catch {
      return DEFAULT_MAP_CENTER
    }
  })
  const [mapZoom, setMapZoom] = useState(() => {
    try {
      return Number(sessionStorage.getItem('rapidry_livemap_zoom')) || 11
    } catch {
      return 11
    }
  })

  const { keyMissing, isLoaded, loadError } = useMapsReady()

  const fetchData = useCallback(async () => {
    try {
      const [agentsRes, ordersRes] = await Promise.all([adminApi.getLiveAgents(), adminApi.getOrdersMapData()])

      const agentsList = Array.isArray(agentsRes?.data) ? agentsRes.data : []
      setAgents(
        agentsList.map((row: Record<string, unknown>) => ({
          id: String(row.id),
          name: (row.name as string | null) || null,
          phone: (row.phone as string | null) || null,
          current_lat: row.current_lat == null ? null : Number(row.current_lat),
          current_lng: row.current_lng == null ? null : Number(row.current_lng),
          last_seen_at: (row.last_seen_at as string | null) || null,
          zone: (row.zone as string | null) || null,
          active_orders_count: Number(row.active_orders_count) || 0,
          lifecycle_status: (row.lifecycle_status as LiveAgent['lifecycle_status']) || undefined,
        })),
      )

      const ordersList = Array.isArray(ordersRes?.data) ? ordersRes.data : []
      setOrders(
        ordersList
          .map((row: Record<string, unknown>) => ({
            id: String(row.id),
            order_number: String(row.order_number || ''),
            customer_name: (row.customer_name as string | undefined) || undefined,
            status: String(row.status || ''),
            lat: Number(row.lat),
            lng: Number(row.lng),
            address_line: (row.address_line as string | undefined) || undefined,
            agent_name: (row.agent_name as string | undefined) || undefined,
          }))
          .filter((o: OrdersMapOrder) => !['delivered', 'cancelled', 'returned'].includes(o.status)),
      )

      setError(null)
    } catch {
      setError('Unable to load map data.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchData()
    const id = window.setInterval(() => void fetchData(), REFRESH_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [fetchData])

  const coalesceTimersRef = useRef<Record<string, number>>({})
  const pendingLocationRef = useRef<Record<string, LocationEventPayload>>({})
  const prevRecordedAtRef = useRef<Record<string, number>>({})

  const applyAgentLocation = useCallback((payload: LocationEventPayload) => {
    const agentId = String(payload.agent_id || '')
    if (!agentId) return

    const nowRecorded = payload.recorded_at ? new Date(payload.recorded_at).getTime() : Date.now()
    const prevRecorded = prevRecordedAtRef.current[agentId] || nowRecorded
    prevRecordedAtRef.current[agentId] = nowRecorded

    const delta = Math.max(300, Math.min(nowRecorded - prevRecorded, 5000))
    setAgentAnimationDuration((prev) => ({ ...prev, [agentId]: delta }))

    setAgents((prev) =>
      prev.map((a) =>
        a.id === agentId
          ? {
              ...a,
              current_lat: payload.lat == null ? a.current_lat : Number(payload.lat),
              current_lng: payload.lng == null ? a.current_lng : Number(payload.lng),
              last_seen_at: payload.recorded_at || new Date().toISOString(),
            }
          : a,
      ),
    )
  }, [])

  const handleLocationUpdated = useCallback(
    (payload: LocationEventPayload) => {
      if (!payload?.agent_id) return
      const agentId = String(payload.agent_id)
      pendingLocationRef.current[agentId] = payload
      if (coalesceTimersRef.current[agentId]) return

      coalesceTimersRef.current[agentId] = window.setTimeout(() => {
        const pending = pendingLocationRef.current[agentId]
        if (pending) applyAgentLocation(pending)
        delete pendingLocationRef.current[agentId]
        delete coalesceTimersRef.current[agentId]
      }, 1000)
    },
    [applyAgentLocation],
  )

  const handleAgentStatusChanged = useCallback(
    (payload: Record<string, unknown>) => {
      // agent:status_changed carries agent_id; agent:online_changed carries agentId.
      const rawId = payload && (payload.agent_id || payload.agentId)
      if (rawId && payload.is_online === false) {
        const agentId = String(rawId)
        setFadingAgentIds((prev) => ({ ...prev, [agentId]: true }))
        const delay = reduceMotion ? 0 : 1000
        setTimeout(() => {
          setAgents((prev) => prev.filter((a) => a.id !== agentId))
          setFadingAgentIds((prev) => {
            const next = { ...prev }
            delete next[agentId]
            return next
          })
        }, delay)
        return
      }
      void fetchData()
    },
    [fetchData, reduceMotion],
  )

  const handleOrderStatusChanged = useCallback(
    (payload: Record<string, unknown>) => {
      if (!payload?.order_id) return
      const terminalStates = ['delivered', 'cancelled', 'returned']
      const orderId = String(payload.order_id)
      if (terminalStates.includes(String(payload.status))) {
        setFadingOrderIds((prev) => ({ ...prev, [orderId]: true }))
        const delay = reduceMotion ? 0 : 1000
        setTimeout(() => {
          setOrders((prev) => prev.filter((o) => o.id !== orderId))
          setFadingOrderIds((prev) => {
            const next = { ...prev }
            delete next[orderId]
            return next
          })
        }, delay)
      } else {
        void fetchData()
      }
    },
    [fetchData, reduceMotion],
  )

  useSocket({
    'agent:location_updated': handleLocationUpdated,
    'agent:online_changed': handleAgentStatusChanged,
    'agent:status_changed': handleAgentStatusChanged,
    'order:status_changed': handleOrderStatusChanged,
    'order:assigned': () => {
      void fetchData()
    },
    'order:agent_assigned': () => {
      void fetchData()
    },
  })

  const mappableAgents = useMemo(() => {
    return agents.filter((a) => {
      if (a.current_lat == null || a.current_lng == null) return false
      if (filterStatus === 'idle' && a.active_orders_count > 0) return false
      if (filterStatus === 'active' && a.active_orders_count === 0) return false
      if (filterZone !== 'all' && a.zone !== filterZone) return false
      if (filterAgent !== 'all' && a.id !== filterAgent) return false
      if (
        filterSearch &&
        !a.name?.toLowerCase().includes(filterSearch.toLowerCase()) &&
        !a.phone?.includes(filterSearch)
      ) {
        return false
      }
      return true
    })
  }, [agents, filterStatus, filterZone, filterAgent, filterSearch])

  const mappableOrders = useMemo(() => {
    return orders.filter((o) => {
      if (!o.lat || !o.lng) return false
      if (filterSearch && !o.order_number.toLowerCase().includes(filterSearch.toLowerCase())) return false
      return true
    })
  }, [orders, filterSearch])

  const selectedAgent = useMemo(
    () => mappableAgents.find((a) => a.id === selectedAgentId) ?? null,
    [mappableAgents, selectedAgentId],
  )

  const zoneOptions = useMemo(
    () => Array.from(new Set(agents.map((a) => a.zone).filter(Boolean) as string[])),
    [agents],
  )
  const agentOptions = useMemo(
    () => mappableAgents.map((a) => ({ id: a.id, label: a.name || a.phone || a.id })),
    [mappableAgents],
  )

  const shouldCluster = mappableAgents.length + mappableOrders.length > 50

  const handleMapIdle = useCallback((map: google.maps.Map) => {
    const center = map.getCenter()
    const zoom = map.getZoom()
    if (center) {
      const centerObj = { lat: center.lat(), lng: center.lng() }
      setMapCenter(centerObj)
      sessionStorage.setItem('rapidry_livemap_center', JSON.stringify(centerObj))
    }
    if (zoom) {
      setMapZoom(zoom)
      sessionStorage.setItem('rapidry_livemap_zoom', zoom.toString())
    }
  }, [])

  const updateFilter = (key: string, value: string) => {
    setSearchParams((prev) => {
      if (value === 'all' || !value) prev.delete(key)
      else prev.set(key, value)
      return prev
    })
  }

  const deriveAgentColor = (agent: LiveAgent) => {
    const status = agent.lifecycle_status || (agent.active_orders_count === 0 ? 'idle' : 'assigned')
    if (status === 'out_for_delivery') return COLOR_OUT_FOR_DELIVERY
    if (status === 'picked_up') return COLOR_PICKED_UP
    if (status === 'en_route') return COLOR_EN_ROUTE
    if (status === 'assigned') return COLOR_ASSIGNED
    return COLOR_IDLE
  }

  if (keyMissing) return <MapUnavailable reason="keyMissing" height={520} />
  if (loadError) return <MapUnavailable reason="loadError" height={520} />
  if (!isLoaded) {
    return (
      <div className="font-body" style={{ padding: '20px', color: colors.textMuted }}>
        Loading map...
      </div>
    )
  }

  return (
    <div style={{ display: 'grid', gap: '20px' }}>
      <header
        style={{
          alignItems: 'center',
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '16px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          justifyContent: 'space-between',
          padding: '16px 20px',
        }}
      >
        <div>
          <h1 className="font-display" style={{ color: colors.textPrimary, fontSize: '22px', margin: 0 }}>
            Live Map
          </h1>
          <p className="font-body" style={{ color: colors.textMuted, fontSize: '13px', margin: '4px 0 0' }}>
            {mappableAgents.length} agents · {mappableOrders.length} active orders
          </p>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search agent or order..."
            value={filterSearch}
            onChange={(e) => updateFilter('search', e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              background: colors.bgPrimary,
              color: colors.textPrimary,
              fontSize: '13px',
              fontFamily: 'inherit',
            }}
          />
          <select
            value={filterStatus}
            onChange={(e) => updateFilter('status', e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              background: colors.bgPrimary,
              color: colors.textPrimary,
              fontSize: '13px',
            }}
          >
            <option value="all">All Status</option>
            <option value="active">Busy Agents</option>
            <option value="idle">Idle Agents</option>
          </select>
          <select
            value={filterZone}
            onChange={(e) => updateFilter('zone', e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              background: colors.bgPrimary,
              color: colors.textPrimary,
              fontSize: '13px',
            }}
          >
            <option value="all">All Zones</option>
            {zoneOptions.map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </select>
          <select
            value={filterAgent}
            onChange={(e) => updateFilter('agent', e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              background: colors.bgPrimary,
              color: colors.textPrimary,
              fontSize: '13px',
            }}
          >
            <option value="all">All Agents</option>
            {agentOptions.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => void fetchData()}
            disabled={loading}
            className="font-body"
            style={{
              alignItems: 'center',
              background: colors.bgPrimary,
              border: `1px solid ${colors.border}`,
              borderRadius: '10px',
              color: colors.textPrimary,
              cursor: 'pointer',
              display: 'inline-flex',
              fontSize: '13px',
              fontWeight: 600,
              gap: '8px',
              padding: '10px 14px',
            }}
          >
            <RefreshCw size={14} aria-hidden />
            Refresh
          </button>
        </div>
      </header>

      {error ? (
        <div
          className="font-body"
          style={{
            alignItems: 'center',
            background: 'rgba(220,38,38,0.08)',
            border: '1px solid rgba(220,38,38,0.4)',
            borderRadius: '12px',
            color: '#DC2626',
            display: 'flex',
            gap: '10px',
            padding: '12px 16px',
          }}
        >
          <AlertCircle size={16} aria-hidden />
          {error}
        </div>
      ) : null}

      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          zoom={mapZoom}
          center={mapCenter}
          onLoad={(map) => { mapRef.current = map }}
          onIdle={() => { if (mapRef.current) handleMapIdle(mapRef.current) }}
        >
          {shouldCluster ? (
            <MarkerClustererF>
              {(clusterer) => (
                <>
                  {mappableAgents.map((agent) => (
                    <AnimatedAgentMarker
                      key={`agent-${agent.id}`}
                      targetLat={agent.current_lat as number}
                      targetLng={agent.current_lng as number}
                      icon={buildAgentIcon(deriveAgentColor(agent), fadingAgentIds[agent.id] ? 0.2 : 1)}
                      onClick={() => setSelectedAgentId(agent.id)}
                      clusterer={clusterer}
                      durationMs={agentAnimationDuration[agent.id] || 1000}
                      reduceMotion={reduceMotion}
                    />
                  ))}
                  {mappableOrders.map((order) => (
                    <MarkerF
                      key={`order-${order.id}`}
                      position={{ lat: order.lat, lng: order.lng }}
                      icon={buildOrderIcon(fadingOrderIds[order.id] ? 0.2 : 1)}
                      onClick={() => setSelectedOrderId(order.id)}
                      clusterer={clusterer}
                    />
                  ))}
                </>
              )}
            </MarkerClustererF>
          ) : (
            <>
              {mappableAgents.map((agent) => (
                <AnimatedAgentMarker
                  key={`agent-${agent.id}`}
                  targetLat={agent.current_lat as number}
                  targetLng={agent.current_lng as number}
                  icon={buildAgentIcon(deriveAgentColor(agent), fadingAgentIds[agent.id] ? 0.2 : 1)}
                  onClick={() => setSelectedAgentId(agent.id)}
                  durationMs={agentAnimationDuration[agent.id] || 1000}
                  reduceMotion={reduceMotion}
                />
              ))}
              {mappableOrders.map((order) => (
                <MarkerF
                  key={`order-${order.id}`}
                  position={{ lat: order.lat, lng: order.lng }}
                  icon={buildOrderIcon(fadingOrderIds[order.id] ? 0.2 : 1)}
                  onClick={() => setSelectedOrderId(order.id)}
                />
              ))}
            </>
          )}

          {selectedAgent && selectedAgent.current_lat != null && selectedAgent.current_lng != null ? (
            <InfoWindowF
              position={{
                lat: selectedAgent.current_lat,
                lng: selectedAgent.current_lng,
              }}
              onCloseClick={() => setSelectedAgentId(null)}
            >
              <div style={{ minWidth: 220, padding: '4px 6px' }}>
                <div style={{ alignItems: 'center', display: 'flex', gap: '8px', marginBottom: '6px' }}>
                  <Bike size={16} color="#0F2E2A" aria-hidden />
                  <strong style={{ fontSize: '14px', color: '#0F2E2A' }}>{selectedAgent.name || 'Agent'}</strong>
                </div>
                <div style={{ fontSize: '12px', color: '#475569', lineHeight: 1.5 }}>
                  <div>{selectedAgent.phone || '-'}</div>
                  <div>Zone: {selectedAgent.zone || '-'}</div>
                  <div>Active orders: {selectedAgent.active_orders_count}</div>
                  <div>Last seen: {formatLastSeen(selectedAgent.last_seen_at)}</div>
                  <div style={{ marginTop: '6px', fontWeight: 600 }}>In-progress Orders</div>
                  {mappableOrders
                    .filter((o) => o.agent_name && selectedAgent.name && o.agent_name === selectedAgent.name)
                    .slice(0, 5)
                    .map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#1d4ed8',
                          cursor: 'pointer',
                          padding: 0,
                          display: 'block',
                        }}
                        onClick={() => setSelectedOrderId(o.id)}
                      >
                        {o.order_number}
                      </button>
                    ))}
                </div>
              </div>
            </InfoWindowF>
          ) : null}
        </GoogleMap>
      </div>

      {selectedOrderId ? (
        <OrderDetailPanel
          order={{ id: selectedOrderId, order_number: selectedOrderId, status: 'searching_agent', total: 0 }}
          onClose={() => setSelectedOrderId(null)}
          onAssignAgent={() => {}}
          onReorderSuccess={() => {}}
        />
      ) : null}
    </div>
  )
}
